// WithdrawPermissionForm — "Record that {signer} withdrew permission to contact"
// (RRA v2.1 A4). The channel, the date they told us, and ONE neutral line of
// evidence (5-300 characters). Recording it stops every open request to a
// verifier and tells each person we had contacted, in writing; the request
// itself stays open. The pure pieces (today, the date text, the success notice)
// are exported and unit-tested; the API call is made by the caller.

import { useState } from 'react';
import type { RraPermissionWithdrawalView, RraWithdrawPermissionBody, RraWithdrawalChannel } from '@/lib/rraStaffClient';

export const WITHDRAWAL_NOTE_MIN = 5;
export const WITHDRAWAL_NOTE_MAX = 300;
/** The counter shows once the line is this long. */
export const WITHDRAWAL_NOTE_COUNTER_FROM = 250;

export const PERMISSION_WITHDRAW_CHANNELS: Array<{ value: RraWithdrawalChannel; label: string }> = [
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'in_person', label: 'In person' },
  { value: 'letter', label: 'Letter' },
  { value: 'other', label: 'Other' },
];

/** Today's calendar date (YYYY-MM-DD) on the browser's clock — the date input's default and its `max`. */
export function todayYmd(now: Date = new Date()): string {
  const y = String(now.getFullYear()).padStart(4, '0');
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * A recorded calendar date as words ("Oct 22, 2026"). A date-only value (or a
 * midnight-UTC stamp of one) is a CALENDAR day, so it is read as that day and
 * never shifted back by the browser's time zone.
 */
export function formatCalendarDate(value: string | undefined): string {
  if (!value) return '—';
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})(?:T00:00:00(?:\.0+)?Z)?$/.exec(value);
  if (dateOnly) {
    const local = new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
    return local.toLocaleDateString('en-US', opts);
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString('en-US', opts);
}

/** The success notice: how many people we had contacted were told in writing (none = just the stop). */
export function withdrawalNotice(notified: RraPermissionWithdrawalView['notified'] | undefined): string {
  const n = (notified ?? []).filter((x) => x.sent).length;
  if (n < 1) return 'Recorded. Every open request to a verifier was stopped.';
  const who = n === 1 ? '1 person we had contacted was' : `${n} people we had contacted were`;
  return `Recorded. Every open request to a verifier was stopped, and ${who} told in writing.`;
}

/** Ready to record: a channel is a given; the date is a real day, not in the future; the line is 5-300 characters. */
export function withdrawalFormValid(f: { receivedAt: string; note: string }, today: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(f.receivedAt) || f.receivedAt > today) return false;
  const len = f.note.trim().length;
  return len >= WITHDRAWAL_NOTE_MIN && len <= WITHDRAWAL_NOTE_MAX;
}

export function WithdrawPermissionForm({
  busy,
  running,
  onSubmit,
  onCancel,
}: {
  /** True while any console action is running (disables the buttons). */
  busy: boolean;
  /** True while THIS form's action is the one running (the button's label). */
  running: boolean;
  onSubmit: (body: RraWithdrawPermissionBody) => void;
  onCancel: () => void;
}): React.JSX.Element {
  const today = todayYmd();
  const [channel, setChannel] = useState<RraWithdrawalChannel>('email');
  const [receivedAt, setReceivedAt] = useState(today);
  const [note, setNote] = useState('');
  const valid = withdrawalFormValid({ receivedAt, note }, today);

  return (
    <div className="card" style={{ marginTop: 'var(--space-3)', background: 'var(--surface-2, transparent)' }}>
      <div className="field">
        <label className="label" htmlFor="perm-wd-channel">How they told us</label>
        <select
          id="perm-wd-channel"
          className="input"
          value={channel}
          onChange={(e) => setChannel(e.target.value as RraWithdrawalChannel)}
        >
          {PERMISSION_WITHDRAW_CHANNELS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
      <div className="field">
        <label className="label" htmlFor="perm-wd-date">When</label>
        <input
          id="perm-wd-date"
          className="input"
          type="date"
          max={today}
          value={receivedAt}
          onChange={(e) => setReceivedAt(e.target.value)}
        />
      </div>
      <div className="field">
        <label className="label" htmlFor="perm-wd-note">Evidence (one line)</label>
        <input
          id="perm-wd-note"
          className="input"
          maxLength={WITHDRAWAL_NOTE_MAX}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        {note.length >= WITHDRAWAL_NOTE_COUNTER_FROM && (
          <div style={{ color: 'var(--fg-muted)', fontSize: 'var(--text-xs)' }}>
            {note.length}/{WITHDRAWAL_NOTE_MAX}
          </div>
        )}
      </div>
      <div className="cluster" style={{ gap: 'var(--space-2)' }}>
        <button
          type="button"
          className="btn"
          data-variant="primary"
          data-size="sm"
          disabled={busy || !valid}
          onClick={() => onSubmit({ channel, receivedAt, note: note.trim() })}
        >
          {running ? 'Recording…' : 'Record the withdrawal'}
        </button>
        <button type="button" className="btn" data-variant="ghost" data-size="sm" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
